import { Locales } from '@/plugins/i18n';
import FormStore from '@/store/formStore';
import { FormState } from '@/store/types';
import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import { getModule } from 'vuex-module-decorators';
import { isEmpty } from 'lodash';
import { RouteNames } from './types';

Vue.use(VueRouter);

export const localizedPaths: Record<RouteNames, { [key in Locales]: string }> = {
  [RouteNames.Crossroad]: {
    [Locales.CS]: '/',
  },
  [RouteNames.Documents]: {
    [Locales.CS]: '/dokumenty',
  },
  [RouteNames.LoanValue]: {
    [Locales.CS]: '/vyse-pujcky',
  },
  [RouteNames.PersonData]: {
    [Locales.CS]: '/osobni-udaje',
  },
  [RouteNames.Banking]: {
    [Locales.CS]: '/vypisy',
  },
  [RouteNames.Cover]: {
    [Locales.CS]: '/zajisteni',
  },
  [RouteNames.NotFound]: {
    [Locales.CS]: '*',
  },
};

const localizedRoutes: { [key in Locales]: RouteConfig[] } = {
  [Locales.CS]: [
    {
      path: localizedPaths.Crossroad.cs,
      name: RouteNames.Crossroad,
      component: () => import(/* webpackChunkName: "crossroad" */ '@/views/Crossroad.vue'),
    },
    {
      path: localizedPaths.LoanValue.cs,
      name: RouteNames.LoanValue,
      component: () => import(/* webpackChunkName: "loan-value" */ '@/views/personInfo/LoanValue.vue'),
    },
    {
      path: localizedPaths.PersonData.cs,
      name: RouteNames.PersonData,
      component: () => import(/* webpackChunkName: "person-data" */ '@/views/personInfo/PersonData.vue'),
    },
    {
      path: localizedPaths.Banking.cs,
      name: RouteNames.Banking,
      component: () => import(/* webpackChunkName: "banking" */ '@/views/BankingInfo.vue'),
    },
    {
      path: localizedPaths.Cover.cs,
      name: RouteNames.Cover,
      component: () => import(/* webpackChunkName: "cover" */ '@/views/collateralInfo/Cover.vue'),
    },
    {
      path: localizedPaths.Documents.cs,
      name: RouteNames.Documents,
      component: () => import(/* webpackChunkName: "documents" */ '@/views/Documents.vue'),
    },
    {
      path: localizedPaths.NotFound.cs,
      name: RouteNames.NotFound,
      component: () => import(/* webpackChunkName: "not-found" */ '@/views/NotFound.vue'),
    },
  ],
};

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: localizedRoutes.cs,
});

let formStore: FormStore | null = null;

router.beforeEach(async (to, from, next) => {
  formStore = getModule(FormStore);

  // ROUTE GUARDS
  if (!isEmpty(to.query.clientId) || to.query.token?.includes('?clientId')) {
    // fix url if token includes encoded clientId
    next({ ...to, query: { token: to.query.token.slice(0, 32), screening: 'true' } });
    Vue.$toast.success('Napojení Vaší banky proběhlo úspěšně');
  } else if (formStore.loaded) {
    // When first load isn't done, don't do route guarging
    const token = to.query.token || formStore.token;

    if (to.name === RouteNames.Crossroad) next();
    else if (to.name === RouteNames.LoanValue) next();
    else if (to.name === RouteNames.PersonData) next();
    // If token is set up in query or store, then route is secured in App.vue
    else if (to.name === RouteNames.Banking && token && formStore.formState === FormState.PersonInfo) next();
    else if (to.name === RouteNames.Cover && token && formStore.formState === FormState.BankingInfo) next();
    else if (to.name === RouteNames.Documents && token) next();
    else next({ name: RouteNames.Crossroad });
  } else next();
});

let retryCount = Number(localStorage.getItem('retryCount')) || 0;

router.onError((error) => {
  if (/loading chunk \d* failed./i.test(error.message) || error.name === 'ChunkLoadError') {
    if (retryCount < 3) {
      retryCount++;
      localStorage.setItem('retryCount', retryCount?.toString());
      window.location.reload();
    } else {
      localStorage.removeItem('retryCount');
      throw new Error('Failed to load chunk after 3 attempts.');
    }
  }
});

export default router;
